import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Eye, EyeClosed, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { catchAxiosError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import { authRepo } from "@/lib/repositories";

const formSchema = z
  .object({
    password: z.string().min(8),
    confirmPassword: z.string().min(1),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ["confirmPassword"],
    message: "password_mismatch",
  });

interface CompleteResetFormProps {
  email: string;
  code: string;
  onSuccess: () => void;
}

const CompleteResetForm = ({
  email,
  code,
  onSuccess,
}: CompleteResetFormProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true);
    const [error] = await catchAxiosError(
      authRepo.completePasswordReset(email, code, data.password),
    );
    setIsLoading(false);
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    onSuccess();
  }

  return (
    <div className="w-full max-w-112.5 flex flex-col gap-12">
      <form id="complete-reset-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="new-password-input">
                  {t("modules.auth.forgot_password.password_label")}
                </FieldLabel>
                <InputGroup className="h-12">
                  <InputGroupInput
                    {...field}
                    aria-invalid={fieldState.invalid}
                    type={showPassword ? "text" : "password"}
                    id="new-password-input"
                    autoComplete="new-password"
                  />
                  <InputGroupAddon
                    align="inline-end"
                    className="cursor-pointer select-none"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <EyeClosed /> : <Eye />}
                  </InputGroupAddon>
                </InputGroup>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name="confirmPassword"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="confirm-password-input">
                  {t("modules.auth.forgot_password.confirm_password_label")}
                </FieldLabel>
                <InputGroup className="h-12">
                  <InputGroupInput
                    {...field}
                    aria-invalid={fieldState.invalid}
                    type={showConfirm ? "text" : "password"}
                    id="confirm-password-input"
                    autoComplete="new-password"
                  />
                  <InputGroupAddon
                    align="inline-end"
                    className="cursor-pointer select-none"
                    onClick={() => setShowConfirm((prev) => !prev)}
                  >
                    {showConfirm ? <EyeClosed /> : <Eye />}
                  </InputGroupAddon>
                </InputGroup>
                {fieldState.invalid && (
                  <FieldError
                    errors={[
                      fieldState.error?.message === "password_mismatch"
                        ? {
                            ...fieldState.error,
                            message: t(
                              "modules.auth.forgot_password.password_mismatch",
                            ),
                          }
                        : fieldState.error,
                    ]}
                  />
                )}
              </Field>
            )}
          />
        </FieldGroup>
      </form>
      <Button
        disabled={isLoading}
        type="submit"
        form="complete-reset-form"
        className="h-16 text-lg"
      >
        {isLoading && <Loader className="animate-spin" />}
        {t("modules.auth.forgot_password.submit_reset")}
      </Button>
    </div>
  );
};

export default CompleteResetForm;
